
import { FormCtx, Tag, TagsDiff } from "./types";

export function buildChangedFields<T extends Record<string, any>>(
  current: T,
  original: T,
  fieldNames: string[]
): Partial<T> {
  const changed: Partial<T> = {};
  fieldNames.forEach((name) => {
    if (current[name] !== original[name]) {
      changed[name as keyof T] = current[name];
    }
  });
  return changed;
}

export function buildTagsDiff(
  currentTags: Tag[],
  originalTags: Tag[]
): TagsDiff {
  const originalNames = new Set(
    originalTags.map((t) => t.entity_tag_name.toLowerCase())
  );
  const currentIds = new Set(
    currentTags
      .filter((t) => t.entity_tag_id !== undefined)
      .map((t) => t.entity_tag_id)
  );

  const insert = currentTags
    .filter((t) => !originalNames.has(t.entity_tag_name.toLowerCase()))
    .map((t) => t.entity_tag_name);

  const del = originalTags
    .filter(
      (t) =>
        t.entity_tag_id !== undefined && !currentIds.has(t.entity_tag_id)
    )
    .map((t) => t.entity_tag_id as number);

  return { insert, delete: del };
}

export function hasAnyChanges<T extends Record<string, any>>(
  current: T,
  original: T,
  fieldNames: string[]
): boolean {
  return fieldNames.some((name) => current[name] !== original[name]);
}

export interface ProductsServicesPayloadOptions {
  build: (items: any[], tenantId: string | null) => any[];
  tenantId: string | null;
  payloadKey?: string;
}

export interface EntityPayloadOptions<T> {
  fields: string[];
  selectFields?: string[];
  rename?: Record<string, string>;
  originalSnapshot?: Record<string, any> | null;
  productsServices?: ProductsServicesPayloadOptions;
  extra?: (ctx: FormCtx<T>, mode: "create" | "edit") => Record<string, any>;
}

export async function buildEntityPayload<T extends Record<string, any>>(
  ctx: FormCtx<T>,
  mode: "create" | "edit",
  opts: EntityPayloadOptions<T>
): Promise<any> {
  const isSelectField = (name: string) => !!opts.selectFields?.includes(name);
  const extractValue = (name: string) => {
    const raw = (ctx.values as Record<string, any>)[name];
    return isSelectField(name) ? raw?.value ?? null : raw;
  };
  const payloadKey = (name: string) => opts.rename?.[name] || name;

  const tagsDiff = ctx.getTagsDiff();
  const customFieldsArray = await ctx.getCustomFieldsPayload();
  const validProductsServices = opts.productsServices
    ? opts.productsServices.build(ctx.productsServices, opts.productsServices.tenantId)
    : [];
  const productsServicesKey = opts.productsServices?.payloadKey || "product_n_service";

  if (mode === "create") {
    const payload: Record<string, any> = {};
    opts.fields.forEach((name) => {
      payload[payloadKey(name)] = extractValue(name);
    });
    Object.assign(payload, opts.extra ? opts.extra(ctx, "create") : {});
    if (tagsDiff.insert.length > 0) {
      payload.tags = { insert: tagsDiff.insert };
    }
    if (validProductsServices.length > 0) {
      payload[productsServicesKey] = validProductsServices;
    }
    if (customFieldsArray.length > 0) {
      payload.custom_fields = customFieldsArray;
    }
    return payload;
  }

  const changes: Record<string, any> = {};
  if (opts.originalSnapshot) {
    opts.fields.forEach((name) => {
      const current = extractValue(name);
      const original = opts.originalSnapshot![name];
      if (current !== original) {
        changes[payloadKey(name)] = current;
      }
    });
  }
  Object.assign(changes, opts.extra ? opts.extra(ctx, "edit") : {});
  if (tagsDiff.insert.length > 0 || tagsDiff.delete.length > 0) {
    changes.tags = tagsDiff;
  }
  if (validProductsServices.length > 0) {
    changes[productsServicesKey] = validProductsServices;
  }
  if (customFieldsArray.length > 0) {
    changes.custom_fields = customFieldsArray;
  }
  return changes;
}
